import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import Home from "./pages/Home";
import Team from "./pages/Team";
import Leadership from "./pages/team/Leadership";
import RnD from "./pages/team/RnD";
import BatteryRnD from "./pages/team/BatteryRnD";
import SolarPower from "./pages/team/SolarPower";
import HRIT from "./pages/team/HRIT";
import Portfolio from "./pages/Portfolio";
import Stub from "./pages/Stub";
import Dharshana from "./pages/Dharshana";
import Audit from "./pages/Audit";
import Partner from "./pages/Partner";
import DataRoom from "./pages/DataRoom";
import Partners from "./pages/Partners";
import Reserve from "./pages/Reserve";
import Visit from "./pages/Visit";
import ReservationPolicy from "./pages/ReservationPolicy";
import Admin from "./pages/Admin";
import Vision from "./pages/Vision";
import SEO from "./components/SEO";
import ScrollToTop from "./components/ScrollToTop";

const pageVariants = {
  initial: { opacity: 0, y: 14, scale: 0.995 },
  enter: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.998,
    transition: { duration: 0.24, ease: [0.65, 0, 0.35, 1] },
  },
};

function AppShell({ children, testId, seo }) {
  return (
    <motion.div
      key={testId}
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      data-testid={`route-${testId}`}
      style={{ minHeight: "100vh" }}
    >
      {/* SEO — React 19 hoists <title> and <meta> into <head> automatically. */}
      {seo && <SEO {...seo} />}
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <AppShell
              testId="home"
              seo={{
                title: "Green Gold Energy — An exclusive allocation. Not a fundraise.",
                description:
                  "The Green Gold Grid. Solar + Indian Battery, opt-in community ownership, managed by griddata. 720 MWh/yr BESS plant at SIPCOT Coimbatore.",
              }}
            >
              <Home />
            </AppShell>
          }
        />

        {/* Vision — canonical URL for the vision page (was /dharshana in v1).
            /dharshana still routes here for backwards compat; the 12 old
            /dharshana/{col-row} deep links are redirected to /vision hashes
            inside the Dharshana component so no external link 404s. */}
        <Route
          path="/vision"
          element={
            <AppShell
              testId="vision"
              seo={{
                title: "The Vision — Green Gold Energy",
                description:
                  "Green Gold Grid: a vertically integrated, horizontally distributed energy ecosystem — three axes, four scales, twelve doors.",
              }}
            >
              <Vision />
            </AppShell>
          }
        />
        <Route
          path="/dharshana"
          element={
            <AppShell
              testId="dharshana"
              seo={{
                title: "The Vision — Green Gold Energy",
                description:
                  "Green Gold Grid: a vertically integrated, horizontally distributed energy ecosystem — three axes, four scales, twelve doors.",
              }}
            >
              <Dharshana />
            </AppShell>
          }
        />
        <Route
          path="/dharshana/:slug"
          element={
            <AppShell
              testId="dharshana-cell"
              seo={{
                title: "The Vision — Green Gold Energy",
                description:
                  "Green Gold Grid: a vertically integrated, horizontally distributed energy ecosystem — three axes, four scales, twelve doors.",
              }}
            >
              <Dharshana />
            </AppShell>
          }
        />
        <Route
          path="/audit"
          element={
            <AppShell
              testId="audit"
              seo={{
                title: "Sovereign Audit — Green Gold Energy",
                description:
                  "See what a Solar+BESS or BESS-only system saves for your industrial site. From ₹8-12/kWh to ₹4-5.50/kWh.",
              }}
            >
              <Audit />
            </AppShell>
          }
        />
        <Route
          path="/partner"
          element={
            <AppShell
              testId="partner"
              seo={{
                title: "SPV Data Room — Green Gold Energy",
                description:
                  "Allocating co-ownership of a strategic ₹850 Cr plant. BOOT + SPV. Request access.",
              }}
            >
              <Partner />
            </AppShell>
          }
        />
        <Route
          path="/data-room"
          element={
            <AppShell
              testId="data-room"
              seo={{
                title: "Funder Cockpit — Green Gold Energy",
                description: "By invitation.",
                robots: "noindex,nofollow",
              }}
            >
              <DataRoom />
            </AppShell>
          }
        />
        <Route
          path="/data-room/access"
          element={
            <AppShell
              testId="data-room-access"
              seo={{
                title: "Funder Cockpit — Green Gold Energy",
                description: "By invitation.",
                robots: "noindex,nofollow",
              }}
            >
              <DataRoom />
            </AppShell>
          }
        />
        <Route
          path="/partners"
          element={
            <AppShell
              testId="partners"
              seo={{
                title: "Partner Portal — Green Gold Energy",
                description:
                  "EPCs, NBFCs, and energy auditors: register a deal, claim referral priority.",
              }}
            >
              <Partners />
            </AppShell>
          }
        />
        <Route
          path="/reserve"
          element={
            <AppShell
              testId="reserve"
              seo={{
                title: "Reserve a Sovereign Block — Green Gold Energy",
                description: "Co-own a share of a community energy asset near you.",
              }}
            >
              <Reserve />
            </AppShell>
          }
        />
        <Route
          path="/visit"
          element={
            <AppShell
              testId="visit"
              seo={{
                title: "Schedule a Tour — Green Gold Energy",
                description:
                  "Visit our Tamil Nadu Factory — the factory runs on its own solar + storage.",
              }}
            >
              <Visit />
            </AppShell>
          }
        />
        <Route
          path="/policies/reservation"
          element={
            <AppShell
              testId="policy-reservation"
              seo={{
                title: "Reservation policy — Green Gold Energy",
                description: "Interim reservation and refund policy for Sovereign Blocks.",
                robots: "noindex,nofollow",
              }}
            >
              <ReservationPolicy />
            </AppShell>
          }
        />

        <Route
          path="/team"
          element={
            <AppShell
              testId="team"
              seo={{
                title: "Team — Green Gold Energy",
                description:
                  "The founding team and the IIT R&D bench behind the Green Gold Grid — Leadership, R&D, Battery R&D, Solar Power and HR/IT.",
              }}
            >
              <Team />
            </AppShell>
          }
        />
        <Route
          path="/team/leadership"
          element={
            <AppShell
              testId="team-leadership"
              seo={{
                title: "Leadership Team — Green Gold Energy",
                description:
                  "Founders and Chief Officers driving Green Gold Energy's vision for sustainable energy solutions.",
              }}
            >
              <Leadership />
            </AppShell>
          }
        />
        <Route
          path="/team/rnd"
          element={
            <AppShell
              testId="team-rnd"
              seo={{
                title: "R&D Team — Green Gold Energy",
                description:
                  "Engineers and IIT professors innovating in embedded systems, PCB design and software for renewable energy.",
              }}
            >
              <RnD />
            </AppShell>
          }
        />
        <Route
          path="/team/battery-rnd"
          element={
            <AppShell
              testId="team-battery-rnd"
              seo={{
                title: "Battery R&D Team — Green Gold Energy",
                description:
                  "Cell chemistry, cell design, testing, BMS, scale-up — the multidisciplinary Battery R&D team.",
              }}
            >
              <BatteryRnD />
            </AppShell>
          }
        />
        <Route
          path="/team/solar-power"
          element={
            <AppShell
              testId="team-solar-power"
              seo={{
                title: "Solar Power Plant Team — Green Gold Energy",
                description:
                  "Utility-scale solar EPC and O&M team across Electrical, Mechanical, Civil and Land Acquisition & Substation.",
              }}
            >
              <SolarPower />
            </AppShell>
          }
        />
        <Route
          path="/team/hr-it"
          element={
            <AppShell
              testId="team-hr-it"
              seo={{
                title: "HR & IT Team — Green Gold Energy",
                description:
                  "People operations and internal systems keeping every desk connected.",
              }}
            >
              <HRIT />
            </AppShell>
          }
        />
        <Route
          path="/portfolio"
          element={
            <AppShell
              testId="portfolio"
              seo={{
                title: "Portfolio — Green Gold Energy",
                description:
                  "50+ solar, BESS, floating, agrivoltaic and hybrid projects across 31 countries — from utility-scale ground-mount to research stations in Svalbard.",
              }}
            >
              <Portfolio />
            </AppShell>
          }
        />

        <Route
          path="/admin"
          element={
            <AppShell
              testId="admin"
              seo={{
                title: "Admin — Green Gold Energy",
                description: "Restricted.",
                robots: "noindex,nofollow",
              }}
            >
              <Admin />
            </AppShell>
          }
        />

        {/* Fallback — send anything unknown home. */}
        <Route
          path="*"
          element={
            <AppShell
              testId="home-fallback"
              seo={{
                title: "Green Gold Energy — An exclusive allocation. Not a fundraise.",
                description:
                  "Green Gold Grid: 720 MWh/yr BESS plant at SIPCOT Coimbatore. Co-owned SPV. BOOT-structured.",
              }}
            >
              <Home />
            </AppShell>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AnimatedRoutes />
      </BrowserRouter>
    </div>
  );
}
